import {preserveSourceTextOptOuts} from '@waka/compat/source-text-delete';
import {
  findAllLinkedItems,
  findOrphanedTransitions,
} from '../../utils/find-linked-items';
import type { EditorState } from '../types';
import { setSelectedItems } from './set-selected-items';
import { removeTimelineTime } from '@waka/compat/timeline-ripple';

const deleteItemsNative = (
  state: EditorState,
  idsToDelete: string[],
  options: { isLinkingEnabled?: boolean; timingApplied?: boolean; internalDetach?: boolean } = {},
): EditorState => {
  if (!options.timingApplied) {
    const roots = state.undoableState.tracks
      .filter((track) => track.magnetic && !track.locked)
      .flatMap((track) =>
        track.items
          .filter((id) => idsToDelete.includes(id))
          .map((id) => ({ id, track, item: state.undoableState.items[id] })),
      )
      .filter((entry) => entry.item);
    const mains = roots.filter(
      (entry) =>
        entry.track.metadata?.isMainTrack &&
        entry.track.metadata.type === 'video',
    );
    if (options.isLinkingEnabled && mains.length) {
      const result = removeTimelineTime(
        state,
        state.undoableState.tracks.map((track) => track.id),
        mains.map(({ item }) => ({
          start: item!.from,
          end: item!.from + item!.durationInFrames,
        })),
      );
      if (!result.ok) return state;
      const remaining = idsToDelete
        .filter((id) => !mains.some((main) => main.id === id))
        .flatMap((id) => result.replacements[id] ?? [id]);
      return remaining.length
        ? deleteItems(result.state, remaining, {
            isLinkingEnabled: false,
            timingApplied: true,
            internalDetach:options.internalDetach,
          })
        : result.state;
    }
    if (roots.length) {
      let changed = state;
      for (const track of state.undoableState.tracks) {
        const selected = roots.filter((root) => root.track.id === track.id);
        if (!selected.length) continue;
        const result = removeTimelineTime(
          changed,
          [track.id],
          selected.map(({ item }) => ({
            start: item!.from,
            end: item!.from + item!.durationInFrames,
          })),
        );
        if (!result.ok) return state;
        changed = result.state;
      }
      const rest = idsToDelete.filter(
        (id) => !roots.some((root) => root.id === id),
      );
      return rest.length
        ? deleteItems(changed, rest, {
            isLinkingEnabled: false,
            timingApplied: true,
            internalDetach:options.internalDetach,
          })
        : changed;
    }
  }
  const { items, tracks, transitions } = state.undoableState;
  const locked = (id: string, seen = new Set<string>()): boolean => {
    if (seen.has(id)) return true;
    const next = new Set(seen).add(id);
    if (tracks.some((track) => track.locked && track.items.includes(id)))
      return true;
    return Object.values(items).some(
      (parent) =>
        parent.type === 'group' &&
        parent.children.includes(id) &&
        locked(parent.id, next),
    );
  };
  const selected = idsToDelete.filter((id) => items[id] && !locked(id));
  const candidates = options.isLinkingEnabled
    ? findAllLinkedItems({ targetItemIds: selected, tracks, items })
    : selected;
  const removed = new Set<string>();
  function descendants(id: string, seen = new Set<string>()): string[] | null {
    if (seen.has(id) || locked(id)) return null;
    const item = items[id];
    if (!item) return [];
    if (item.type !== 'group') return [id];
    const result = [id];
    const next = new Set(seen).add(id);
    for (const child of item.children) {
      const children = descendants(child, next);
      if (!children) return null;
      result.push(...children);
    }
    return result;
  }
  for (const id of candidates) {
    const ids = descendants(id);
    if (ids) ids.forEach((key) => removed.add(key));
  }
  if (!removed.size) return state;
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of Object.values(items)) {
      if (
        item.type === 'group' &&
        !removed.has(item.id) &&
        item.children.length &&
        item.children.every((id) => removed.has(id))
      ) {
        removed.add(item.id);
        changed = true;
      }
    }
  }
  const nextItems = { ...items };
  for (const id of removed) delete nextItems[id];
  for (const item of Object.values(nextItems))
    if (item.type === 'group' && item.children.some((id) => removed.has(id)))
      nextItems[item.id] = {
        ...item,
        children: item.children.filter((id) => !removed.has(id)),
      };
  const nextTransitions = { ...transitions };
  for (const id of findOrphanedTransitions({
    itemIds: [...removed],
    transitions,
  }))
    delete nextTransitions[id];
  return setSelectedItems(
    {
      ...state,
      undoableState: {
        ...state.undoableState,
        items: nextItems,
        transitions: nextTransitions,
        // A clip is a placement. Deleting it never deletes library originals or their recovery history.
        tracks: tracks.map((track) =>
          track.items.some((id) => removed.has(id))
            ? { ...track, items: track.items.filter((id) => !removed.has(id)) }
            : track,
        ),
      },
    },
    state.selectedItems.filter((id) => !removed.has(id)),
  );
};

export const deleteItems=(state:EditorState,ids:string[],options:{isLinkingEnabled?:boolean;timingApplied?:boolean;internalDetach?:boolean}={}):EditorState=>{
 if(options.internalDetach)return deleteItemsNative(state,ids,options);
 const prepared=preserveSourceTextOptOuts(state,ids);
 return prepared.ids.length?deleteItemsNative(prepared.state,prepared.ids,options):prepared.state;
};
